import { motion } from "framer-motion";
import { Headset, Shield } from "lucide-react";
import { Button } from "../components/ui/button";

const LoginPage = () => {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 relative overflow-hidden">
      {/* Background decoration - TalentDesk navy/yellow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#425B76]/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#F5C518]/15 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#425B76]/5 rounded-full blur-3xl" />
      </div>
      
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-10">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#425B76] to-[#2D4156] shadow-lg shadow-[#425B76]/30 mb-6"
            >
              <Headset className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">
              Beakon
            </h1>
            <p className="text-slate-500 text-sm mb-1">
              AI-powered support operations platform
            </p>
            <p className="text-[#425B76] text-xs font-medium">
              Powered by TalentDesk
            </p>
          </div>

          {/* Login Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 p-8"
          >
            <div className="text-center mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Sign in to your account
              </h2>
              <p className="text-slate-500 text-sm">
                Access your team&apos;s support dashboard
              </p>
            </div>

            <Button
              onClick={handleLogin}
              data-testid="google-login-btn"
              className="w-full h-12 bg-gradient-to-r from-[#425B76] to-[#2D4156] hover:from-[#2D4156] hover:to-[#1E2D3D] text-white font-medium rounded-lg flex items-center justify-center gap-3 transition-all shadow-lg shadow-[#425B76]/25"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400">
              <Shield className="w-3.5 h-3.5" />
              <span>Secured with enterprise-grade authentication</span>
            </div>
          </motion.div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-10 grid grid-cols-3 gap-4 text-center"
          >
            {[
              { id: "hubspot", label: "HubSpot", sublabel: "Ticket Sync" },
              { id: "ai-draft", label: "AI Draft", sublabel: "Responses" },
              { id: "analytics", label: "Real-time", sublabel: "Analytics" }
            ].map((feature) => (
              <div key={feature.id} className="p-4 rounded-xl bg-white/70 backdrop-blur border border-[#425B76]/10 shadow-sm">
                <p className="text-slate-900 font-medium text-sm">{feature.label}</p>
                <p className="text-slate-400 text-xs">{feature.sublabel}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
